import { useState } from 'react'
import { type FieldErrors, type UseFormRegister } from 'react-hook-form'

import { FormTextFieldDS } from 'components/reusable/form/FormFields'
import { CollapsibleContainer, SolidCircleCheckIcon } from 'slab'
import {
  useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdCarrierAgreementsArbitration as useGetCarrierArbitrationAgreement,
  useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdCarrierAgreementsContractAgreement as useGetCarrierContractAgreement,
} from 'utils/web_api'

import { useOnboarding } from '../OnboardingContext'
import { type ConfirmEnrollmentFormData } from '../types'

function ConfirmEnrollmentAgreementCard ({
  register,
  errors,
  content,
  isFormSubmitting,
  type,
  isEverythingValid,
  carrierName,
  defaultOpen,
}: {
  register: UseFormRegister<ConfirmEnrollmentFormData>;
  errors: FieldErrors<ConfirmEnrollmentFormData>;
  content: string;
  isFormSubmitting: boolean;
  type: 'arbitration' | 'contract';
  isEverythingValid: boolean;
  carrierName?: string | null;
  defaultOpen?: boolean;
}) {
  const { onboardingData } = useOnboarding()
  const [open, setOpen] = useState<boolean>(defaultOpen ?? false)

  const heading =
    type === 'arbitration'
      ? 'Carrier arbitration agreement'
      : 'Carrier contract agreement'

  const subtitle =
    type === 'arbitration'
      ? (carrierName || 'Your medical plan carrier') +
        ' requires that you read and sign this arbitration agreement to process your enrollment with them. ' +
        'You must provide a signature.'
      : 'Please read the following contract agreement. You must provide a signature.'

  const fieldPrefix =
    type === 'arbitration' ? 'arbitration_agreement' : 'contract_agreement'

  const selfName = (onboardingData?.member?.participant_name || '')

  return (
    <CollapsibleContainer
      open={open}
      onOpenChange={setOpen}
      heading={heading}
      headingIconContent={
        isEverythingValid && (
          <div className='text-green-400'>
            <SolidCircleCheckIcon aria-label='(complete)' size={16} />
          </div>
        )
      }
    >
      <div className='p-4'>
        <p className='text-xs leading-[18px] tracking-[0px] text-gray-500 mb-2'>
          {subtitle}
        </p>
        <div className='overflow-y-auto h-[140px] border border-gray-400 rounded-lg p-3 mb-6'>
          <p
            className='text-xs leading-[18px] tracking-[0px] text-gray-800 has-[ol]:pl-3'
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
        <div className='flex flex-col gap-y-6'>
          <FormTextFieldDS<ConfirmEnrollmentFormData>
            fieldId={`${fieldPrefix}.self_signature`}
            fieldLabel={`Signature (${selfName})`}
            register={register}
            placeholder='Type the name above'
            error={errors[fieldPrefix]?.self_signature}
            disabled={isFormSubmitting}
            validationRule={{
              required: 'Signature for self is required',
              validate: (value: string) =>
                value.trim().toLocaleLowerCase() === selfName.trim().toLocaleLowerCase() || 'Signature must match your name',
            }}
          />
        </div>
      </div>
    </CollapsibleContainer>
  )
}

export function ArbitrationAgreementCard ({
  register,
  errors,
  isFormSubmitting,
  isEverythingValid,
  carrierName,
  defaultOpen,
}: {
  register: any;
  errors: any;
  isFormSubmitting: boolean;
  isEverythingValid: boolean;
  carrierName?: string | null;
  defaultOpen?: boolean;
}) {
  const { onboardingData } = useOnboarding()
  const { data: arbitrationAgreement } = useGetCarrierArbitrationAgreement(
    onboardingData?.enrollment_event?.id || '',
    {
      query: {
        retry: false,
      },
    }
  )

  if (!arbitrationAgreement) return null

  return (
    <ConfirmEnrollmentAgreementCard
      register={register}
      errors={errors}
      type='arbitration'
      content={arbitrationAgreement.content || ''}
      isFormSubmitting={isFormSubmitting}
      isEverythingValid={isEverythingValid}
      carrierName={carrierName}
      defaultOpen={defaultOpen}
    />
  )
}

export function ContractAgreementCard ({
  register,
  errors,
  isFormSubmitting,
  isEverythingValid,
  defaultOpen,
}: {
  register: any;
  errors: any;
  isFormSubmitting: boolean;
  isEverythingValid: boolean;
  defaultOpen?: boolean;
}) {
  const { onboardingData } = useOnboarding()
  const { data: contractAgreement } = useGetCarrierContractAgreement(
    onboardingData?.enrollment_event?.id || '',
    {
      query: {
        retry: false,
      },
    }
  )

  if (!contractAgreement) return null

  return (
    <ConfirmEnrollmentAgreementCard
      register={register}
      errors={errors}
      type='contract'
      content={contractAgreement.content || ''}
      isFormSubmitting={isFormSubmitting}
      isEverythingValid={isEverythingValid}
      defaultOpen={defaultOpen}
    />
  )
}
